import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import withApiError, {
  ERROR_TYPE_REPORTING,
} from 'layers/errorHandling/apiError'
import PropTypes from 'prop-types'
import { SpinnerAnimatedIcon } from 'secondstep-components'
import {
  operations as reportsOperations,
  selectors as reportsSelectors,
} from 'store/reportsManager'
import { operations as ssapiOperations } from 'store/ssapiManager'
import { TOGGLE_VIEW } from 'store/userSessionManager/constants'
import { LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL } from 'components/AdminSiteHeader/constants'
import { selectors as licenseSelectors } from 'store/licenseManager'
import AdminMultiSiteView from './component'
import { LoadingWrapper } from './component.styles'

export const AdminMultiSiteViewContainer = ({
  currentPage,
  email,
  fetchAllPublishFlows,
  fetchLessonCompletionData,
  isFetchingLessons,
  lessonCompletionToggleView,
  lessonCompletionUnitsLabel,
  orderBy,
  programKey,
  showLessonCompletionExport,
  sites,
  sitesPerPage,
  sitesPreferences,
  sortKey,
  summary,
  totalCount,
  totalPages,
  updateMonitorSelectedPaginationCurrentPage,
  updateMonitorSelectedPaginationSortKey,
  updateMonitorSelectedPaginationSortOrder,
  updateDwbMonitorSelectedPaginationCurrentPage,
  updateDwbMonitorSelectedPaginationSortKey,
  updateDwbMonitorSelectedPaginationSortOrder,
  useDwbMonitorPagination,
}) => {
  useEffect(() => {
    if (email && programKey) {
      fetchLessonCompletionData({
        adminEmail: email,
        toggleView: lessonCompletionToggleView,
        program: programKey,
      })
    }
  }, [lessonCompletionToggleView, email, programKey])

  useEffect(() => {
    if (email) {
      fetchAllPublishFlows()
    }
  }, [email, fetchAllPublishFlows])

  const getPaginatedSites = (page, key, order) => {
    const updateSortKey = useDwbMonitorPagination
      ? updateDwbMonitorSelectedPaginationSortKey
      : updateMonitorSelectedPaginationSortKey
    const updateSortOrder = useDwbMonitorPagination
      ? updateDwbMonitorSelectedPaginationSortOrder
      : updateMonitorSelectedPaginationSortOrder
    const updateCurrentPage = useDwbMonitorPagination
      ? updateDwbMonitorSelectedPaginationCurrentPage
      : updateMonitorSelectedPaginationCurrentPage

    if (key !== sortKey) updateSortKey(key)
    if (order !== orderBy) updateSortOrder(order)
    if (page !== currentPage) updateCurrentPage(page)
  }

  if (isFetchingLessons) {
    return (
      <LoadingWrapper>
        <SpinnerAnimatedIcon />
        <p>Loading...</p>
      </LoadingWrapper>
    )
  }

  if (!Array.isArray(sites) || !summary) {
    return null
  }

  return (
    <AdminMultiSiteView
      currentPage={currentPage}
      getPaginatedSites={getPaginatedSites}
      isFetchingLessons={isFetchingLessons}
      lessonCompletionUnitsLabel={lessonCompletionUnitsLabel}
      orderBy={orderBy}
      showLessonCompletionExport={showLessonCompletionExport}
      sites={sites}
      sitesPerPage={sitesPerPage}
      sitesPreferences={sitesPreferences}
      sortKey={sortKey}
      summary={summary}
      totalCount={totalCount}
      totalPages={totalPages}
    />
  )
}

const mapDispatchToProps = {
  fetchAllPublishFlows: ssapiOperations.fetchAllPublishFlows,
  fetchLessonCompletionData: reportsOperations.fetchLessonCompletionData,
  updateMonitorSelectedPaginationCurrentPage:
    reportsOperations.updateMonitorSelectedPaginationCurrentPage,
  updateMonitorSelectedPaginationSortKey:
    reportsOperations.updateMonitorSelectedPaginationSortKey,
  updateMonitorSelectedPaginationSortOrder:
    reportsOperations.updateMonitorSelectedPaginationSortOrder,
  updateDwbMonitorSelectedPaginationCurrentPage:
    reportsOperations.updateDwbMonitorSelectedPaginationCurrentPage,
  updateDwbMonitorSelectedPaginationSortKey:
    reportsOperations.updateDwbMonitorSelectedPaginationSortKey,
  updateDwbMonitorSelectedPaginationSortOrder:
    reportsOperations.updateDwbMonitorSelectedPaginationSortOrder,
}

export const mapStateToProps = state => {
  const {
    reportsManager: {
      admin,
      isFetchingAdminLessonCompletionData: isFetchingLessons,
    },
    userProfileManager: { profile },
  } = state
  const { email } = profile
  const programKey = reportsSelectors.selectAdminLessonCompletionProgramKey(
    state,
  )
  const lessonCompletionToggleView = reportsSelectors.selectAdminLessonCompletionToggleView(
    state,
  )
  const sites = reportsSelectors.selectSortedAdminLessonCompletionData(state)
  const adminSitesWithDwb = reportsSelectors.selectAdminLessonCompletionDwbSites(
    state,
  )
  const useDwbMonitorPagination =
    lessonCompletionToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS &&
    Array.isArray(adminSitesWithDwb) &&
    adminSitesWithDwb.length > 0

  const sitesPreferences = licenseSelectors.selectActiveAdminSitePreferences(
    state,
  )
  const summary = reportsSelectors.selectAdminLessonCompletionSummaryData(state)

  const monitorPagination = useDwbMonitorPagination
    ? admin.dwbMonitorPagination
    : admin.monitorPagination
  const {
    currentPage,
    sitesPerPage,
    totalCount,
    totalPages,
    sortKey,
    orderBy,
  } = monitorPagination

  const summaryForView =
    useDwbMonitorPagination && summary
      ? { ...summary, sitesTotal: totalCount }
      : summary

  const lessonCompletionUnitsLabel =
    lessonCompletionToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS
      ? LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL || 'Specialized Units'
      : 'Core Units'

  const showLessonCompletionExport =
    lessonCompletionToggleView !== TOGGLE_VIEW.SPECIALIZED_UNITS

  return {
    currentPage,
    email,
    isFetchingLessons,
    lessonCompletionToggleView,
    lessonCompletionUnitsLabel,
    orderBy,
    programKey: programKey,
    showLessonCompletionExport,
    sites,
    sitesPerPage,
    sitesPreferences,
    sortKey,
    summary: summaryForView,
    totalCount,
    totalPages,
    useDwbMonitorPagination,
  }
}

AdminMultiSiteViewContainer.propTypes = {
  currentPage: PropTypes.number,
  email: PropTypes.string,
  fetchAllPublishFlows: PropTypes.func,
  fetchLessonCompletionData: PropTypes.func,
  isFetchingLessons: PropTypes.bool,
  lessonCompletionToggleView: PropTypes.string,
  lessonCompletionUnitsLabel: PropTypes.string.isRequired,
  orderBy: PropTypes.string,
  programKey: PropTypes.string,
  showLessonCompletionExport: PropTypes.bool,
  sites: PropTypes.array,
  sitesPerPage: PropTypes.number,
  sitesPreferences: PropTypes.object,
  sortKey: PropTypes.string,
  summary: PropTypes.object,
  totalCount: PropTypes.number,
  totalPages: PropTypes.number,
  updateDwbMonitorSelectedPaginationCurrentPage: PropTypes.func,
  updateDwbMonitorSelectedPaginationSortKey: PropTypes.func,
  updateDwbMonitorSelectedPaginationSortOrder: PropTypes.func,
  updateMonitorSelectedPaginationCurrentPage: PropTypes.func,
  updateMonitorSelectedPaginationSortKey: PropTypes.func,
  updateMonitorSelectedPaginationSortOrder: PropTypes.func,
  useDwbMonitorPagination: PropTypes.bool,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withApiError(AdminMultiSiteViewContainer, [ERROR_TYPE_REPORTING]))
